import { isBoolean, isUndefined } from 'lodash';
import { InstanceData, InstanceDataType } from '@/framework/data';
import { BaseClassInstance, IEditorConfig, IExportPlugin, IRegisterPluginOptions, PluginType } from '@/version-types';
import { getBehaviorKey } from '../behavior';

export class RegisterServer {
    private systemPlugins: Record<string, IExportPlugin> = {};
    private viewportPlugins: Record<string, IExportPlugin> = {};
    private stagePlugins: Record<string, IExportPlugin> = {};
    /** 代码实例 */
    private instances: Map<string, unknown> = new Map();
    /** 已加载插件列表 */
    private loadedList: Set<string> = new Set();
    /** 禁用的列表 */
    private disableList: Set<string> = new Set();
    /** 数据初始化列表 */
    destroyList: ((args: { identity: string; getInstance: (classConstructor: any) => any }) => void)[] = [];
    /** 历史记录 */
    historyList: ((args: { identity: string }) => InstanceType<BaseClassInstance>[])[] = [];

    constructor(
        private identity: string,
        private config: IEditorConfig,
    ) {}

    /** 设置实例 */
    private setInstance<T extends BaseClassInstance>(name: string, instance: InstanceType<T>) {
        if (this.instances.get(name)) {
            console.error(`${name} 实例已存在，请检查是否重名，或者重复实例化`);
        }
        this.instances.set(name, instance);
    }

    /** 根据类名获取实例 */
    getInstance<T extends BaseClassInstance>(classConstructor: T): InstanceType<T> {
        return this.instances.get(classConstructor.name) as InstanceType<T>;
    }

    /** 根据名称获取实例 */
    getInstanceByName<T>(name: string) {
        return this.instances.get(name) as T;
    }

    /** 获取插件 */
    getPlugins() {
        return {
            system: Object.values(this.systemPlugins),
            viewport: Object.values(this.viewportPlugins),
            stage: Object.values(this.stagePlugins),
        };
    }

    /** 创建插件代码实例 */
    private registerInstance(plugin: IExportPlugin, rootIsEnable: boolean) {
        if (this.loadedList.has(plugin.name)) {
            console.log(`⚠️${plugin.name} 依赖加载`);
            return;
        }
        console.log(`✔ ${plugin.name} 初始化`);
        this.loadedList.add(plugin.name);

        // 加载自定义历史记录
        if (plugin.createHistory) {
            this.historyList.push(plugin.createHistory);
        }

        // 加载状态
        if (plugin.createState) {
            const res = plugin.createState();
            for (const item of res) {
                if (InstanceData.getRecordItem(this.identity, InstanceDataType.State, item.name)) {
                    throw new Error(`${item.name} 状态已存在`);
                }
                InstanceData.setRecordItem(
                    this.identity,
                    InstanceDataType.State,
                    item.name,
                    item.createState(this.identity),
                );
            }
        }

        // 加载依赖插件
        if (plugin.dependencies) {
            const res = plugin.dependencies();
            this.registerPlugins(res, rootIsEnable);
        }

        // 数据初始化
        if (plugin.destroy) {
            this.destroyList.push(plugin.destroy);
        }

        // 创建代码实例
        if (plugin.createInstance) {
            const res = plugin.createInstance({ identity: this.identity });
            console.log('createInstance=>', res);
            for (const instance of res) {
                if (!instance.key || !instance.value) {
                    console.error(`${plugin.name} 插件实例缺少 key 或 value 属性`);
                } else {
                    this.setInstance(instance.key, instance.value);
                    const name = instance.value.constructor.name;
                    if (name !== instance.key) {
                        this.setInstance(name, instance.value);
                    }
                }
            }
        }

        // 创建图层行为
        if (plugin.createBehavior) {
            const item = plugin.createBehavior();
            if (!item?.layerType) {
                throw new Error(`${plugin.name} 插件缺少 layerType 属性`);
            }
            const behaviorObj = item.getBehavior(this.identity);
            const key = getBehaviorKey(item.layerType, behaviorObj.role);
            console.log('key=>', key);

            if (InstanceData.getRecordItem(this.identity, InstanceDataType.Behaviors, key)) {
                console.log(`${item.layerType} 行为已注册过`);
                return;
            }
            InstanceData.setRecordItem(this.identity, InstanceDataType.Behaviors, key, behaviorObj);
        }
    }

    /** 设置插件 */
    registerPlugins(configs: IRegisterPluginOptions[], rootIsEnable?: boolean) {
        for (const config of configs) {
            const plugin = config.plugin;
            const name = plugin.name;

            if (!name) {
                console.error(`插件 ${name} 未设置 name 属性`);
                continue;
            }
            // console.log('---===>', this.loadedList);
            const isEnable = isUndefined(rootIsEnable) ? (config.enable ?? true) : rootIsEnable;
            this.registerInstance(plugin, isEnable);

            if (plugin.type === PluginType.LAYER && !plugin.createBehavior) {
                throw new Error(`${name} 插件需要实现 createBehavior 方法`);
            }
            if (plugin.type === PluginType.EXTENSION) continue;
            if (isBoolean(rootIsEnable) && !rootIsEnable) continue;
            if (!plugin.render) continue;

            // 设置功能插件
            if (config.enable ?? true) {
                if (this.disableList.has(name)) continue;
                if (plugin.type === PluginType.SYSTEM) {
                    this.systemPlugins[name] = plugin;
                } else if (plugin.type === PluginType.VIEWPORT) {
                    this.viewportPlugins[name] = plugin;
                } else if (plugin.type === PluginType.STAGE) {
                    this.stagePlugins[name] = plugin;
                }
            } else {
                this.disableList.add(name);
                if (plugin.type === PluginType.SYSTEM) {
                    delete this.systemPlugins[name];
                } else if (plugin.type === PluginType.VIEWPORT) {
                    delete this.viewportPlugins[name];
                } else if (plugin.type === PluginType.STAGE) {
                    delete this.stagePlugins[name];
                }
            }
        }

        console.log('registerPlugins=>', this.getPlugins());
    }
}
